import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { formatCurrency, statusBadgeClass, statusLabel } from './radarUtils';

const MargemPage = () => {
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(false);
  const [busca, setBusca] = useState('');
  const [secao, setSecao] = useState('todas');
  const [ordenacao, setOrdenacao] = useState('impacto_desc');
  const [data, setData] = useState({ itens: [], impacto_30d_total: 0 });

  const loadData = async () => {
    try {
      setLoading(true);
      const params = status === 'all' ? {} : { status };
      const response = await axios.get('/api/radar-lucro/margem', { params });
      setData(response.data || { itens: [], impacto_30d_total: 0 });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar margem');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [status]);

  const secoes = useMemo(() => {
    const set = new Set((data.itens || []).map((i) => i.secao).filter(Boolean));
    return Array.from(set).sort((a, b) => String(a).localeCompare(String(b)));
  }, [data.itens]);

  const itensFiltrados = useMemo(() => {
    const texto = busca.trim().toLowerCase();
    const base = (data.itens || []).filter((item) => {
      const matchBusca =
        !texto ||
        String(item.descricao || '').toLowerCase().includes(texto) ||
        String(item.codigoean || '').toLowerCase().includes(texto);
      const matchSecao = secao === 'todas' || (item.secao || '') === secao;
      return matchBusca && matchSecao;
    });
    const sorted = [...base];
    if (ordenacao === 'impacto_desc') sorted.sort((a, b) => Number(b.impacto_30d || 0) - Number(a.impacto_30d || 0));
    if (ordenacao === 'impacto_asc') sorted.sort((a, b) => Number(a.impacto_30d || 0) - Number(b.impacto_30d || 0));
    if (ordenacao === 'estoque_asc') sorted.sort((a, b) => Number(a.estoque_atual || 0) - Number(b.estoque_atual || 0));
    if (ordenacao === 'estoque_desc') sorted.sort((a, b) => Number(b.estoque_atual || 0) - Number(a.estoque_atual || 0));
    return sorted;
  }, [data.itens, busca, secao, ordenacao]);

  const impactoFiltrado = useMemo(() => itensFiltrados.reduce((acc, i) => acc + Number(i.impacto_30d || 0), 0), [itensFiltrados]);

  return (
    <div className="animate-fade-in space-y-4">
      <div className="rounded-xl border border-danger-200 bg-danger-50 p-4">
        <p className="text-sm font-semibold text-danger-800">Margem sob pressao</p>
        <p className="text-2xl font-bold text-danger-900 mt-1">{formatCurrency(impactoFiltrado)} de perda estimada no filtro atual</p>
        <p className="text-sm text-danger-800 mt-1">Preco incorreto ou margem baixa pode destruir lucro mesmo com vendas fortes.</p>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Margem em Perigo</h1>
          <p className="text-gray-600">Itens com preco zerado ou margem abaixo do minimo</p>
        </div>
        <div className="flex gap-2">
          <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="all">Todos</option>
            <option value="critical">Critico</option>
            <option value="alert">Alerta</option>
          </select>
          <button className="btn-secondary" onClick={loadData} disabled={loading}>
            Atualizar
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body grid grid-cols-1 md:grid-cols-4 gap-3">
          <input className="form-input" placeholder="Buscar por descricao ou EAN" value={busca} onChange={(e) => setBusca(e.target.value)} />
          <select className="form-select" value={secao} onChange={(e) => setSecao(e.target.value)}>
            <option value="todas">Todas as secoes</option>
            {secoes.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select className="form-select" value={ordenacao} onChange={(e) => setOrdenacao(e.target.value)}>
            <option value="impacto_desc">Maior impacto</option>
            <option value="impacto_asc">Menor impacto</option>
            <option value="estoque_asc">Estoque asc</option>
            <option value="estoque_desc">Estoque desc</option>
          </select>
          <div className="text-sm text-gray-600 flex items-center">Itens filtrados: {itensFiltrados.length}</div>
        </div>
      </div>

      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>EAN</th>
                <th>Produto</th>
                <th>Secao</th>
                <th>Status</th>
                <th>Preco</th>
                <th>Custo</th>
                <th>Margem %</th>
                <th>Impacto 30d</th>
              </tr>
            </thead>
            <tbody>
              {itensFiltrados.map((item) => (
                <tr key={item.id_produto}>
                  <td>{item.codigoean || '-'}</td>
                  <td>{item.descricao}</td>
                  <td>{item.secao || '-'}</td>
                  <td>
                    <span className={`badge ${statusBadgeClass(item.margem_status)}`}>{statusLabel(item.margem_status)}</span>
                  </td>
                  <td>{item.venda ?? '-'}</td>
                  <td>{item.custo ?? '-'}</td>
                  <td>{item.margem_pct !== null ? `${item.margem_pct}%` : '-'}</td>
                  <td>{formatCurrency(item.impacto_30d)}</td>
                </tr>
              ))}
              {!loading && itensFiltrados.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-gray-500 py-8">
                    Nenhum item com alerta de margem.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MargemPage;
